import React, { useMemo } from "react";
import { SaleOrder } from "@/types/general";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface ViewReservationsSectionProps {
    reservations: SaleOrder.Reservation[];
    lines: SaleOrder.Components.Lines;
    expirationDate: string | null;
}

interface ResolvedReservation {
    product: {
        id: number;
        name: string;
        code: string;
    };
    quantity: number;
}

/**
 * Builds a map of product_id -> product info from the order lines.
 * This allows rebuilding full product data from reservations that only contain product_id.
 */
export function buildProductMapFromLines(lines: SaleOrder.Components.Lines): Map<number, { id: number; name: string; code: string }> {
    const productsMap = new Map<number, { id: number; name: string; code: string }>();

    const processLine = (lineData: SaleOrder.Components.LineData) => {
        const product = lineData.product;
        if (!product || productsMap.has(product.id)) return;

        productsMap.set(product.id, {
            id: product.id,
            name: product.name,
            code: product.code
        });
    };

    lines.forEach(line => {
        if (line.type === "line") {
            processLine(line.data);
        } else if (line.type === "group") {
            line.data.lines.forEach(groupLine => processLine(groupLine.data));
        }
    });

    return productsMap;
}

function ViewReservationRow({ reservation }: { reservation: ResolvedReservation }) {
    const { t } = useTranslation("", { keyPrefix: "sales.orders.general.reservations" });

    return (
        <div className="flex items-center w-full py-2.5 px-4 text-sm transition-colors hover:bg-white/60">
            {/* Product info */}
            <span className="text-accent underline">
                <Link to={`/dashboard/products/${reservation.product.id}/details`}>
                    {reservation.product.code}
                </Link>
            </span>
            <span className="ml-3 text-accent truncate">{reservation.product.name}</span>

            {/* Left to be picked - next to product info */}
            <div className="flex items-center gap-1 ml-4">
                <span className="text-gray-400 whitespace-nowrap font-light">{t("leftToBePicked")}:</span>
                <span className="text-accent">{reservation.quantity}</span>
            </div>
        </div>
    );
}

export default function ViewReservationsSection({ reservations, lines, expirationDate }: ViewReservationsSectionProps) {
    const { t } = useTranslation("", { keyPrefix: "sales.orders.general.reservations" });

    // Build product map from lines to resolve product info
    const productMap = useMemo(() => buildProductMapFromLines(lines), [lines]);

    // Resolve reservations with full product info
    const resolvedReservations = useMemo<ResolvedReservation[]>(() => {
        return reservations
            .map(reservation => {
                const product = productMap.get(reservation.product_id);
                if (!product) return null;

                return {
                    product,
                    quantity: reservation.quantity
                };
            })
            .filter((r): r is ResolvedReservation => r !== null);
    }, [reservations, productMap]);

    // Don't render if no reservations
    if (resolvedReservations.length === 0) return null;

    const formattedExpirationDate = expirationDate 
        ? dayjs(expirationDate).format("DD.MM.YYYY") 
        : null;

    return (
        <div className="bg-[#F9F9F9] rounded-lg overflow-hidden">
            {/* Header */}
            <div className="p-4 pb-2 flex items-center justify-between gap-4">
                <span className="text-lg font-medium text-accent">
                    {t("heading")}
                </span>
                {formattedExpirationDate && (
                    <div className="text-gray-500">
                        <span className="mr-2">{t("expirationDate")}:</span>
                        <span className="text-accent">{formattedExpirationDate}</span>
                    </div>
                )}
            </div>

            {/* Rows */}
            <div className="pb-2">
                {resolvedReservations.map(reservation => (
                    <ViewReservationRow
                        key={reservation.product.id}
                        reservation={reservation}
                    />
                ))}
            </div>
        </div>
    );
}
